export type TableFilterViewport = 'mobile' | 'tablet' | 'desktop';

export interface TableFilterGridRowLayout {
  total_rows: number;
  row_distribution: number[];
  columns: number;
}

export interface TableFilterGridConfig {
  total_items: number;
  calculated_rows: Record<TableFilterViewport, TableFilterGridRowLayout>;
}

export interface TableFilterGridBreakpoint {
  maxColumns: number;
  minWidth: number;
}

export const TABLE_FILTER_GRID_BREAKPOINTS: Record<
  TableFilterViewport,
  TableFilterGridBreakpoint
> = {
  mobile: { maxColumns: 1, minWidth: 0 },
  tablet: { maxColumns: 3, minWidth: 640 },
  desktop: { maxColumns: 7, minWidth: 1024 },
};

/** Oxirgi qatorda 1 ta "yetim" maydon qolmasin (8 → 4+4, 7+1 emas). */
export function optimalFilterColumnCount(
  itemCount: number,
  maxColumns: number,
): number {
  if (itemCount <= 0) {
    return 1;
  }
  if (itemCount <= maxColumns) {
    return itemCount;
  }

  const cap = Math.min(maxColumns, itemCount);
  const candidates: number[] = [];

  for (let cols = cap; cols >= 2; cols--) {
    const lastRow = itemCount % cols || cols;
    if (lastRow === 1) {
      continue;
    }
    candidates.push(cols);
  }

  // Masalan tablet max=3, 7 ta maydon: 3+3+1 dan boshqa yo‘l yo‘q
  if (candidates.length === 0) {
    for (let cols = cap; cols >= 2; cols--) {
      candidates.push(cols);
    }
  }

  if (candidates.length === 0) {
    return 1;
  }

  candidates.sort((a, b) => {
    // 1) Kamroq qator (9 → 5+4, 3+3+3 emas)
    const rowsA = Math.ceil(itemCount / a);
    const rowsB = Math.ceil(itemCount / b);
    if (rowsA !== rowsB) {
      return rowsA - rowsB;
    }
    // 2) Oxirgi qator to‘liqroq bo‘lsin
    const lastA = itemCount % a || a;
    const lastB = itemCount % b || b;
    const imbalanceA = a - lastA;
    const imbalanceB = b - lastB;
    if (imbalanceA !== imbalanceB) {
      return imbalanceA - imbalanceB;
    }
    // 3) Bir xil bo‘lsa — kengroq grid
    return b - a;
  });

  return candidates[0];
}

export function rowDistribution(
  itemCount: number,
  columnCount: number,
): number[] {
  if (itemCount <= 0 || columnCount <= 0) {
    return [];
  }

  const distribution: number[] = [];
  let remaining = itemCount;

  while (remaining > 0) {
    const rowSize = Math.min(columnCount, remaining);
    distribution.push(rowSize);
    remaining -= rowSize;
  }

  return distribution;
}

export function buildTableFilterGridLayout(
  itemCount: number,
  maxColumns: number,
): TableFilterGridRowLayout {
  const columns = optimalFilterColumnCount(itemCount, maxColumns);
  const row_distribution = rowDistribution(itemCount, columns);

  return {
    columns,
    total_rows: row_distribution.length,
    row_distribution,
  };
}

export function buildTableFilterGridConfig(
  itemCount: number,
  breakpoints: Record<TableFilterViewport, TableFilterGridBreakpoint> = TABLE_FILTER_GRID_BREAKPOINTS,
): TableFilterGridConfig {
  return {
    total_items: itemCount,
    calculated_rows: {
      mobile: buildTableFilterGridLayout(
        itemCount,
        breakpoints.mobile.maxColumns,
      ),
      tablet: buildTableFilterGridLayout(
        itemCount,
        breakpoints.tablet.maxColumns,
      ),
      desktop: buildTableFilterGridLayout(
        itemCount,
        breakpoints.desktop.maxColumns,
      ),
    },
  };
}

export function resolveTableFilterViewport(
  width: number,
  breakpoints: Record<TableFilterViewport, TableFilterGridBreakpoint> = TABLE_FILTER_GRID_BREAKPOINTS,
): TableFilterViewport {
  if (width >= breakpoints.desktop.minWidth) {
    return 'desktop';
  }
  if (width >= breakpoints.tablet.minWidth) {
    return 'tablet';
  }
  return 'mobile';
}

export function columnCountForViewport(
  itemCount: number,
  width: number,
  breakpoints: Record<TableFilterViewport, TableFilterGridBreakpoint> = TABLE_FILTER_GRID_BREAKPOINTS,
): number {
  const viewport = resolveTableFilterViewport(width, breakpoints);
  const maxColumns = breakpoints[viewport].maxColumns;
  return optimalFilterColumnCount(itemCount, maxColumns);
}
